import { Box, Typography } from "@mui/material";
import { IModifierGroup } from "../../../types/menu";


export const renderGroupState = (group: IModifierGroup, level: number) => {
    // (1) 이 그룹(및 하위 그룹)의 충족 결과
    const {
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied,
			currentQuantity
    } = checkGroupSatisfiedRecursively(group);

    // (2) 렌더할 태그 (초록/빨강/주황)
    const tagElement = renderRequiredTag({
      isRequired: group.isRequired,
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
			currentQuantity
    });

    // (3) 실제 JSX 렌더
    return (
      <Box
        key={group.path}
        sx={{
          display: 'flex',
          flexDirection: 'column',
					justifyContent: 'flex-start',
					alignItems: 'flex-start',
          borderRadius: '.4rem',
					pl : `${(level -1) * .8}rem`
        }}
      >
				{(group.isRequired || (!group.isRequired && currentQuantity > 0)) && (
        <Box sx={{display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: '.1rem' }}>
            {group.modifierGroupName}
          </Typography>

          {tagElement}
        </Box>)}

        {/* (4) modifiers 반복 출력 */}
        <Box sx={{ ml: 1 }}>
          {group.modifiers.map((modifier) => {
            return (
              <Box key={modifier.path} sx={{ mb: 0 }}>
                {/* (5) modifier 하위에 nestedGroup 존재 시 → 재귀 렌더 */}
                {modifier.hasNestedModifierGroups && modifier.modifiers.length > 0 && (
                  <Box sx={{ ml: 0 }}>
                    {modifier.modifiers.map((nestedGroup) => renderGroupState(nestedGroup, level + 1))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  export const renderTopGroupState = (group: IModifierGroup, groupIdx: number) => {
    // (1) 이 그룹(및 하위 그룹)의 충족 결과
    const {
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied,
    } = checkTopGroupSatisfiedRecursively(group);

    // (2) 렌더할 태그 (초록/빨강/주황)
    const tagElement = renderAllRequiredTag({
      isRequired: group.isRequired,
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied
    });

    // (3) 실제 JSX 렌더
    return (
      <Box
        key={group.path}
        sx={{
          display: 'flex',
          flexDirection: 'column',
					justifyContent: 'flex-start',
					alignItems: 'flex-start',
					flex :1,
        }}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
					<Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', width: '100%', mb: 1}}>
						<Box sx={{position: 'relative', mr: '1.0rem', width: '2.8rem', height: '2.8rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
							<img style={{position: 'absolute', top: 0, left: 0, width: '2.8rem', height: '2.8rem' }}
									src={process.env.PUBLIC_URL+'/assets/images/ellipse.svg'}
									alt="ellipse"
								/>
							<Typography sx={{position: 'relative', fontSize: '1.8rem', fontWeight: '600', color: '#fff', zIndex: '1'}}>{groupIdx + 1}</Typography>
						</Box>
						{tagElement}
					</Box>
          <Typography variant="subtitle1" sx={{ fontSize: '1.5rem', fontWeight: 'bold', mr: '.1rem', textAlign: 'start', width: '100%', ml: '1.0rem' }}>
            {group.modifierGroupName}
          </Typography>
        </Box>
      </Box>
    );
  }

  interface RequiredTagProps {
    isRequired: boolean;
    satisfiesMinRequirement: boolean;
    satisfiesMaxRequirement: boolean;
		currentQuantity: number;
  } 

  const renderRequiredTag =({
    isRequired,
    satisfiesMinRequirement,
    satisfiesMaxRequirement,
		currentQuantity
  }: RequiredTagProps) => {
    if (isRequired) {
      // (a) 필수 그룹
      if (satisfiesMinRequirement && satisfiesMaxRequirement) {
        // min/max 모두 충족 => 초록색 + done.svg
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '10.0rem',
              height: '2.4rem',
              padding: '.2rem .9rem',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '3.0rem',
              background: '#66C03A',
            }}
          >
            <img style={{width: '1.4rem', height: '1.4rem'}}
              src={`${process.env.PUBLIC_URL}/assets/images/done.svg`}
              alt="done"
            />
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                color: '#fff',
                ml: '.1rem',
              }}
            >
              Required
            </Typography>
          </Box>
        );
      } else {
        // (b) 필수 but min/max 불충족 => 빨간색 + warning.svg
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '10.0rem',
              height: '2.4rem',
              padding: '.2rem .9rem',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '3.0rem',
              background: '#F72E13',
            }}
          >
            <img style={{width: '1.4rem', height: '1.4rem'}}
              src={`${process.env.PUBLIC_URL}/assets/images/warning.svg`}
              alt="warning"
            />
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                color: '#fff',
                ml: 1,
              }}
            >
              Required
            </Typography>
          </Box>
        );
      }
    } else {
      // (c) Optional 그룹 => 주황색 배지

      return (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							width: '10.0rem',
							height: '2.4rem',
							padding: '.2rem .9rem',
							justifyContent: 'center',
							alignItems: 'center',
							borderRadius: '3.0rem',
							background: '#FF8E0A',
						}}
					>
						<Typography
							sx={{
								fontSize: '1.4rem',
								fontStyle: 'normal',
								fontWeight: '600',
								lineHeight: 'normal',
								color: '#fff',
							}}
						>
							Optional
						</Typography>
					</Box>
      );
    }
  }

  interface AllRequiredTagProps {
    isRequired: boolean;
		satisfiesMinRequirement: boolean;
    satisfiesMaxRequirement: boolean;
    isFullySatisfied: boolean;
  } 

  const renderAllRequiredTag =({
    isRequired,
		satisfiesMinRequirement,
    satisfiesMaxRequirement,
    isFullySatisfied
  }:AllRequiredTagProps) => {
    if (isRequired) {
      // (a) 필수 그룹
      if (isFullySatisfied) {
        // min/max 모두 충족 => 초록색 + done.svg
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '10.0rem',
              height: '2.4rem',
              padding: '.2rem .9rem',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '3.0rem',
              background: '#66C03A',
            }}
          >
            <img style={{width: '1.4rem', height: '1.4rem'}}
              src={`${process.env.PUBLIC_URL}/assets/images/done.svg`}
              alt="done"
            />
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                color: '#fff',
                ml: '.1rem',
              }}
            >
              Required
            </Typography>
          </Box>
        );
      } else {
        // (b) 필수 but min/max 불충족 => 빨간색 + warning.svg
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '10.0rem',
              height: '2.4rem',
              padding: '.2rem .9rem',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '3.0rem',
              background: '#F72E13',
            }}
          >
            <img style={{width: '1.4rem', height: '1.4rem'}}
              src={`${process.env.PUBLIC_URL}/assets/images/warning.svg`}
              alt="warning"
            />
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                color: '#fff',
                ml: '.1rem',
              }}
            >
              Required
            </Typography>
          </Box>
        );
      }
    } else {
      // (c) Optional 그룹 => 주황색 배지
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '10.0rem',
            height: '2.4rem',
            padding: '.2rem .9rem',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '3.0rem',
            background: '#FF8E0A',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.4rem',
              fontStyle: 'normal',
              fontWeight: '600',
              lineHeight: 'normal',
              color: '#fff',
            }}
          >
            Optional
          </Typography>
        </Box>
      );
    }
  }

  /**
   * 특정 ModifierGroup이 자기 자신(및 하위 그룹) 포함, 
   * min/max/required 조건을 충족하는지 재귀적으로 검사
   *
   * @param group 검사 대상 IModifierGroup
   * @returns { 
	 *	satisfiesMinRequirement: boolean;
	 *	satisfiesMaxRequirement: boolean;
	 *	isFullySatisfied: boolean; 
	 *  currentQuantity: number;
  * }
  */
  const checkGroupSatisfiedRecursively = (group: IModifierGroup) => {
    let satisfiesMinRequirement = true;
    let satisfiesMaxRequirement = true;
    let isFullySatisfied = true;
    let currentQuantity = 0;

    // (1) 현재 그룹의 modifier들 순회
    group.modifiers.forEach((modifier) => {
      // (1-1) 현재 modifier의 quantity 누적
      currentQuantity += modifier.quantity || 0;

      // (1-2) 하위 그룹이 있다면 재귀 체크
      if (modifier.hasNestedModifierGroups && modifier.modifiers.length > 0) {
        modifier.modifiers.forEach((nestedGroup) => {
          const nestedResult = checkGroupSatisfiedRecursively(nestedGroup);
          satisfiesMinRequirement = satisfiesMinRequirement && nestedResult.satisfiesMinRequirement;
          satisfiesMaxRequirement = satisfiesMaxRequirement && nestedResult.satisfiesMaxRequirement;
          isFullySatisfied = isFullySatisfied && nestedResult.isFullySatisfied;
        });
      }
    });

    // (2) 현재 그룹의 min/max 체크
    const minReq = group.minRequired ?? 0;
    const maxReq = group.maxAllowed ?? Infinity;

    if (group.isRequired) {
      if (currentQuantity < minReq) {
        satisfiesMinRequirement = false;
        isFullySatisfied = false;
      }
      if (currentQuantity > maxReq) {
        satisfiesMaxRequirement = false;
        isFullySatisfied = false;
      }
    }

    return {
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied,
      currentQuantity,
    };
  }

  const checkTopGroupSatisfiedRecursively = (group: IModifierGroup) => {
    let satisfiesMinRequirement = true;
    let satisfiesMaxRequirement = true;
    let isFullySatisfied = true;
    let currentQuantity = 0;

    // (1) 현재 그룹의 modifier들 순회
    group.modifiers.forEach((modifier) => {
      // (1-1) 현재 modifier의 quantity 누적
      currentQuantity += modifier.quantity || 0;

      // (1-2) 하위 그룹이 있다면 재귀 체크
      if (modifier.hasNestedModifierGroups && modifier.modifiers.length > 0) {
        modifier.modifiers.forEach((nestedGroup) => {
          const nestedResult = checkGroupSatisfiedRecursively(nestedGroup);
          satisfiesMinRequirement = satisfiesMinRequirement && nestedResult.satisfiesMinRequirement;
          satisfiesMaxRequirement = satisfiesMaxRequirement && nestedResult.satisfiesMaxRequirement;
          isFullySatisfied = isFullySatisfied && nestedResult.isFullySatisfied;
        });
      }
    });

    // (2) 현재 그룹의 min/max 체크
    const minReq = group.minRequired ?? 0;
    const maxReq = group.maxAllowed ?? Infinity;

    if (group.isRequired) {
      if (currentQuantity < minReq) {
        satisfiesMinRequirement = false;
        isFullySatisfied = false;
      }
      if (currentQuantity > maxReq) {
        satisfiesMaxRequirement = false;
        isFullySatisfied = false;
      }
    }

    return {
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied,
      currentQuantity,
    };
  }